import {toast} from 'react-toastify';
import {
    LOCAL_STORAGE_MEMBER_BUSINESS_NAME,
    LOCAL_STORAGE_MEMBER_STORE_TRADING_NAME,
    LOCAL_STORAGE_MEMBER_TRADING_NAME,
    PAGE_MODE,
    SERVER_URL
} from "./Constants";
import printJS from "print-js";
import TillService from "../service/TillService";
import MerchantUserSecurityService from "../service/MerchantUserSecurityService";
import AdminUserSecurityService from "../service/AdminUserSecurityService";


export default class GeneralUtils {
    static isNullOrEmpty(value) {
        if (value !== null && value !== undefined && typeof value === 'number' && Number.isFinite(value)) {
            return false;
        }
        return (!value || value === undefined || value === "" || value === "null" || value.length === 0 || JSON.stringify(value) === JSON.stringify({}));
    }

    /**
     * Dialog kapalıyken veya animasyon sırasında ref henüz mount olmamış olabilir.
     * CSSTransition tamamlanana kadar kısa süre bekleyip callback çalıştırır.
     */
    static invokeRefWhenReady(getRef, callback, attempt = 0) {
        const ref = typeof getRef === 'function' ? getRef() : getRef;
        if (ref) {
            callback(ref);
            return;
        }
        if (attempt >= 15) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('invokeRefWhenReady: component ref was not available after dialog open.');
            }
            return;
        }
        requestAnimationFrame(() => GeneralUtils.invokeRefWhenReady(getRef, callback, attempt + 1));
    }

    static openDialogThenRef(component, stateUpdate, getRef, callback) {
        component.setState(stateUpdate, () => {
            GeneralUtils.invokeRefWhenReady(getRef, callback);
        });
    }

    static escapeHtml(text) {
        if (text === null || text === undefined) {
            return '';
        }
        return String(text)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    /**
     * Member terms: show store trading line only when branch is a real store (not empty / null / -1).
     */
    static isValidMemberMerchantBranchIdForTerms() {
        if (typeof localStorage === 'undefined') {
            return false;
        }
        const id = localStorage.getItem("merchantBranchId");
        if (this.isNullOrEmpty(id)) {
            return false;
        }
        const s = String(id).trim();
        if (s === '-1' || s === 'null') {
            return false;
        }
        const n = Number(s);
        return !(Number.isFinite(n) && n === -1);
    }

    static getMemberBusinessName() {
        if (typeof localStorage === 'undefined') {
            return '';
        }
        const fromBusiness = localStorage.getItem(LOCAL_STORAGE_MEMBER_BUSINESS_NAME);
        if (!this.isNullOrEmpty(fromBusiness)) {
            return String(fromBusiness).trim();
        }
        const legacy = localStorage.getItem(LOCAL_STORAGE_MEMBER_TRADING_NAME);
        return legacy != null ? String(legacy).trim() : '';
    }

    static getMemberStoreTradingName() {
        if (typeof localStorage === 'undefined') {
            return '';
        }
        const trading = localStorage.getItem(LOCAL_STORAGE_MEMBER_STORE_TRADING_NAME);
        if (!this.isNullOrEmpty(trading)) {
            return String(trading).trim();
        }
        return this.getMemberBusinessName();
    }

    /** Legal entity for policies and cardholder-facing payment (merchant of record). */
    static getMemberLegalEntityName() {
        const business = this.getMemberBusinessName();
        if (!this.isNullOrEmpty(business)) {
            return business;
        }
        return this.getMemberStoreTradingName();
    }

    /** Apple Pay / wallet display — prefer registered business name for acquirer compliance. */
    static getMemberPaymentDisplayName(fallbackTradingName) {
        const business = this.getMemberBusinessName();
        if (!this.isNullOrEmpty(business)) {
            return business;
        }
        if (!this.isNullOrEmpty(fallbackTradingName)) {
            return String(fallbackTradingName).trim();
        }
        const trading = this.getMemberStoreTradingName();
        return this.isNullOrEmpty(trading) ? 'Online Store' : trading;
    }

    static persistMemberStoreNamesFromStoreInfo(storeInfo) {
        if (typeof localStorage === 'undefined' || storeInfo == null) {
            return;
        }
        const businessName = storeInfo.businessName != null ? String(storeInfo.businessName).trim() : '';
        const tradingName = storeInfo.tradingName != null ? String(storeInfo.tradingName).trim() : '';
        if (businessName) {
            localStorage.setItem(LOCAL_STORAGE_MEMBER_BUSINESS_NAME, businessName);
            localStorage.setItem(LOCAL_STORAGE_MEMBER_TRADING_NAME, businessName);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_MEMBER_BUSINESS_NAME);
            localStorage.removeItem(LOCAL_STORAGE_MEMBER_TRADING_NAME);
        }
        if (tradingName) {
            localStorage.setItem(LOCAL_STORAGE_MEMBER_STORE_TRADING_NAME, tradingName);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_MEMBER_STORE_TRADING_NAME);
        }
    }

    static clearMemberStoreNames() {
        if (typeof localStorage === 'undefined') {
            return;
        }
        localStorage.removeItem(LOCAL_STORAGE_MEMBER_BUSINESS_NAME);
        localStorage.removeItem(LOCAL_STORAGE_MEMBER_STORE_TRADING_NAME);
        localStorage.removeItem(LOCAL_STORAGE_MEMBER_TRADING_NAME);
    }

    static getMemberLegalContext() {
        const legalEntity = this.getMemberLegalEntityName();
        const tradingName = this.getMemberStoreTradingName();
        const showTradingAs = !this.isNullOrEmpty(legalEntity)
            && !this.isNullOrEmpty(tradingName)
            && tradingName.toLowerCase() !== legalEntity.toLowerCase();
        return {
            legalEntity: legalEntity || 'the restaurant',
            tradingName: tradingName || legalEntity || 'the restaurant',
            showTradingAs
        };
    }

    static getTermsTradingNamePrefixHtml() {
        return this.getTermsMerchantHeaderHtml();
    }

    /** Merchant-of-record header for terms, privacy and cookie policies. */
    static getTermsMerchantHeaderHtml() {
        if (!this.isValidMemberMerchantBranchIdForTerms()) {
            return '';
        }
        const {legalEntity, tradingName, showTradingAs} = this.getMemberLegalContext();
        if (this.isNullOrEmpty(legalEntity) || legalEntity === 'the restaurant') {
            return '';
        }
        let html = "<div style='font-weight: 600; text-align: justify; text-justify: inter-word;'>" +
            this.escapeHtml(legalEntity) + "</div>";
        if (showTradingAs) {
            html += "<div style='text-align: justify; text-justify: inter-word; margin-top: 6px;'>Trading as: " +
                this.escapeHtml(tradingName) + "</div>";
        }
        return html + "<br/>";
    }

    static __isValueEmpty(value) {
        switch (typeof value) {
            case "undefined":
                return true;
            case "object":
                if (value === null) return true;
                return Object.keys.length === 0;
            case "string":
                return (value === undefined || value === null || value.trim() === "");
            default:
                break;
        }
    }

    /** Dashboard orderType when valid; otherwise checkout-computed fallback. */
    static getEffectiveOrderType(fallbackOrderType) {
        const saved = localStorage.getItem("orderType");
        if (!GeneralUtils.__isValueEmpty(saved) && saved !== "undefined") {
            return saved;
        }
        if (!GeneralUtils.isNullOrEmpty(fallbackOrderType)) {
            return fallbackOrderType;
        }
        return null;
    }

    /**
     * Checkout: keep dashboard orderType when still offered; otherwise persist computed default.
     */
    static persistCheckoutOrderType(computedOrderType, deliveryTypesList) {
        const list = Array.isArray(deliveryTypesList) ? deliveryTypesList : [];
        const saved = localStorage.getItem("orderType");
        const hasSaved = !GeneralUtils.__isValueEmpty(saved) && saved !== "undefined";

        if (list.length > 0 && hasSaved && list.some((item) => item && item.id === saved)) {
            return saved;
        }

        if (!hasSaved && !GeneralUtils.isNullOrEmpty(localStorage.getItem("tableBssId"))
            && list.some((item) => item && item.id === "Table Order")) {
            localStorage.setItem("orderType", "Table Order");
            return "Table Order";
        }

        if (!GeneralUtils.isNullOrEmpty(computedOrderType)) {
            localStorage.setItem("orderType", computedOrderType);
            return computedOrderType;
        }

        return computedOrderType;
    }

    static isMobile() {
        let isMobile = false;
        if (navigator.userAgent.match(/Android/i) ||
            navigator.userAgent.match(/iPhone/i)) {
            isMobile = true;
        }
        return isMobile;
    }


    static getDialogStyleForLoginOrContinueAsGuestDialog() {
        return this.isMobile() ? {width: '100%', height: '30%', margin: "5px", textAlign: "center"} : {
            width: '40vw',
            height: '30vh',
            textAlign: "center"
        }
    }

    static getDialogStyleForDialog() {
        return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {
            width: '30vw',
            height: '35vw',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForDepartmentFilter() {
        return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {
            width: 'min(520px, 92vw)',
            height: 'min(540px, 88vh)',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForMerchantBranchFilter() {
        return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {
            width: 'min(960px, 96vw)',
            height: 'min(680px, 90vh)',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForSupplierFilter() {
        return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {
            width: 'min(720px, 94vw)',
            height: 'min(580px, 88vh)',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForReportDialogDetail() {
        return this.isMobile() ? {width: '100%', height: '90%', margin: "10px"} : {
            width: '60vw',
            height: '70vw',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForDialogHalfScreen() {
        return this.isMobile() ? {width: '100%', height: '50%', margin: "10px"} : {
            width: '30vw',
            height: '35vw',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForProduct() {
        return this.isMobile() ? {width: '100%', height: 'auto', maxHeight: '85vh', margin: "10px"} : {
            width: '36rem',
            maxWidth: '95vw',
            height: 'auto',
            maxHeight: '80vh',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static getDialogStyleForPurchaseOrder() {
        if (this.isMobile()) {
            // Mobil cihazlarda height 80% olarak kalır
            return { width: '100%', height: '80%', margin: "10px" };
        }
        if (window.innerWidth <= 801) {
            return {
                width: '90%',
                height: '80%',
                margin: '1px',
                fontSize: '0.85rem',
                lineHeight: '1.2',
            };
        }

        if (window.innerWidth <= 1025) {
            return {
                width: 'auto',         // left ve right ile genişlik ayarlanıyor
                height: '90%',
                margin: "0px",
                position: "fixed",
                top: "10px",           // üstten boşluk
                left: "10px",          // soldan 10px boşluk
                right: "10px",         // sağdan 10px boşluk
                transform: "none"      // default ortalama transform'unu iptal et
            };
        }

        if (window.innerWidth <= 1400) {
            return { width: '80%', height: '90%', marginLeft: "auto", marginRight: "auto" };
        }
        if (window.innerWidth <= 1600) {
            return { width: '70%', height: '65%', marginLeft: "auto", marginRight: "auto" };
        }

        return { width: '50%', height: '70%', marginLeft: "auto", marginRight: "auto" };
    }

    static getDialogStyleForPurchaseRequisitionItem() {
        if (this.isMobile()) {
            return {width: '100%', height: '80%', margin: '10px'};
        }
        return {
            width: 'min(720px, 92vw)',
            maxHeight: '90vh',
            marginLeft: 'auto',
            marginRight: 'auto'
        };
    }



    static getDialogStyleForProductInventory() {
        return this.isMobile() ? {width: '100%', height: '40%', margin: "10px"} : {
            width: '30%',
            height: '40%',
            marginLeft: "auto",
            marginRight: "auto"
        }
    }

    static isSetMenu(item) {
        let isSetMenu = false;
        if (!this.isNullOrEmpty(item.grpOption1) || !this.isNullOrEmpty(item.grpOption2) || !this.isNullOrEmpty(item.grpOption3) || !this.isNullOrEmpty(item.grpOption4) || !this.isNullOrEmpty(item.grpContent) || !this.isNullOrEmpty(item.grpContentX) || !this.isNullOrEmpty(item.grpMain)) {
            isSetMenu = true;
        }
        return isSetMenu
    }

    static getDialogStyleMerchantForDialog() {
        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("scope"))) { // till ekranı icin
            return {width: '90%', height: '90%'};
        } else {
            return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {width: '70%', height: '85%'}
        }

    }

    /** Orders + Order History detail popups — slightly taller than default merchant dialog. */
    static getDialogStyleForOrderDetailDialog() {
        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("scope"))) {
            return {width: '90%', height: '85%', minHeight: '400px'};
        }
        if (this.isMobile()) {
            return {width: '100%', height: '85%', margin: '10px', minHeight: '55vh', maxHeight: '85vh'};
        }
        return {
            width: '70%',
            height: '78%',
            minHeight: '420px',
            maxHeight: '85vh',
            marginLeft: 'auto',
            marginRight: 'auto',
        };
    }

    static getDialogStyleKitchenDisplayForDialog() {
        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("scope"))) { // till ekranı icin
            return {width: '90%', height: '90%'};
        } else {
            return this.isMobile() ? {width: '100%', height: '100%', margin: "10px"} : {width: '40%', height: '60%'}
        }

    }

    // Returns if a value is an object
    static isObject(value) {
        return value && typeof value === 'object' && value.constructor === Object;
    }

    static convertToDownloadableFile = (response) => {
        var sampleArr = this.base64ToArrayBuffer(response.file);
        this.openDocument(response.fileName, sampleArr, response.fileType);
    }

    static printFile = (response) => {
        var sampleArr = this.base64ToArrayBuffer(response.file);
        this.printDocument(response.fileName, sampleArr, response.fileType);
    }

    static getBlob = (response) => {
        var sampleArr = this.base64ToArrayBuffer(response.file);
        var type = response.fileType;
        var blob = new Blob([sampleArr], {type: type});
        var url = URL.createObjectURL(blob);
        return url;
    }
    static downloadFile = (response) => {
        var sampleArr = this.base64ToArrayBuffer(response.file);
        this.saveByteArray(response, sampleArr);
    }

    static saveByteArray = (response, sampleArr) => {
        var blob = new Blob([sampleArr], {type: response.fileType});
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.fileName;
        link.click();
    };

    static base64ToArrayBuffer = (base64) => {
        var binaryString = window.atob(base64);
        var binaryLen = binaryString.length;
        var bytes = new Uint8Array(binaryLen);
        for (var i = 0; i < binaryLen; i++) {
            var ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    }

    static previewDocument = (element) => {
        var reader = new FileReader();
        let file = element.files[0];
        reader.onload = function () {
            var arrayBuffer = this.result;
            var type = file.type;
            var blob = new Blob([arrayBuffer], {type: type});
            var url = URL.createObjectURL(blob);
            window.open(url, "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=600,width=600,height=600");
        }
        reader.readAsArrayBuffer(file);
    }


    static openDocument = (fileName, sampleArr, fileType) => {
        var reader = new FileReader();
        var type = fileType;
        var blob = new Blob([sampleArr], {type: type});
        var url = URL.createObjectURL(blob);
        window.open(url, "_blank", "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=600,width=600,height=600");
    }

    static printDocument = (fileName, sampleArr, fileType) => {
        var reader = new FileReader();
        var type = fileType;
        var blob = new Blob([sampleArr], {type: type});
        var url = URL.createObjectURL(blob);
        printJS(url);
    }

    static convertForImageSource = (file, fileType, fileName) => {
        var sampleArr = this.base64ToArrayBuffer(file);
        return this.getUrl(fileName, sampleArr, fileType);
    }

    static getUrl = (fileName, sampleArr, fileType) => {
        var type = fileType;
        var blob = new Blob([sampleArr], {type: type});
        var url = URL.createObjectURL(blob);
        return url;
    }
    static downloadDocument = (element) => {
        let file = element.files[0];
        var blob = new Blob([file], {type: file.type});
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = file.name;
        link.click();
    }

    static checkPasswordFormat(str) {
        if (str.length > 50) {
            return true;
        }
        var re = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;
        return re.test(str);
    }

    static formatGBP(value) {
        return (value).toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
        });
    }

    static clearCookieAndLocalStorage() {
        let merchantBranchId = localStorage.getItem("merchantBranchId");
        let tableOrderLicense = localStorage.getItem("TABLE_ORDER_LICENSE");
        let webOrderLicense = localStorage.getItem("WEB_ORDER_LICENSE");
        let webMenuLicense = localStorage.getItem("WEB_MENU_LICENSE");
        let orderType = localStorage.getItem("orderType");
        let clientId = localStorage.getItem("clientId");
        let token_url = localStorage.getItem("token_url");
        let orgHref = localStorage.getItem("orgHref");
        let appMode = localStorage.getItem("appMode");
        let basketBssId = localStorage.getItem(localStorage.getItem("merchantBranchId"));


        localStorage.clear();
        this.setJwtToCookie(null, null, null).catch(() => {});
        localStorage.setItem("appMode", appMode);
        localStorage.setItem("merchantBranchId", merchantBranchId);
        localStorage.setItem("TABLE_ORDER_LICENSE", tableOrderLicense);
        localStorage.setItem("WEB_ORDER_LICENSE", webOrderLicense);
        localStorage.setItem("WEB_MENU_LICENSE", webMenuLicense);
        localStorage.setItem("orderType", orderType);
        localStorage.setItem("clientId", clientId);
        localStorage.setItem("token_url", token_url);
        localStorage.setItem("orgHref", orgHref);
        localStorage.setItem(merchantBranchId, basketBssId);

    }

    static getParameterByName(name, url) {
        if (!url) url = window.location.href;
        name = name.replace(/[\[\]]/g, "\\$&");
        var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, " "));
    }

    static tokenRefreshPromise = null;

    static adminTokenRefreshPromise = null;

    static memberTokenRefreshPromise = null;

    /** Till JWT cache: max age in ms (2 minutes) - till service'e sadece bu süre dolunca gidilir (artpos getTokens() ile uyumlu) */
    static TILL_JWT_CACHE_MAX_AGE_MS = 2 * 60 * 1000;
    static TILL_JWT_CACHE_KEY = "tillJwtToken";
    static TILL_JWT_CACHE_TIMESTAMP_KEY = "tillJwtTokenTimestamp";
    static tillTokenFetchPromise = null;

    /**
     * @param {Object} [options]
     * @param {boolean} [options.forceTillRefresh] - TillUser için true ise cache'e bakmadan doğrudan tillService kullanılır (403 retry için)
     */
    static async getJwtFromCookieOrTillService(options = {}) {
        const userType = localStorage.getItem("userType");
        const forceTillRefresh = options.forceTillRefresh === true;

        // 1️⃣ Till User
        if (userType === "tillUser") {
            const showSessionExpiredPage = () => {
                try {
                    sessionStorage.setItem('sessionExpired', '1');
                } catch (e) { /* ignore */ }
                window.dispatchEvent(new CustomEvent('sessionExpired'));
                setTimeout(() => window.dispatchEvent(new CustomEvent('sessionExpired')), 100);
            };

            // TillUser: forceTillRefresh (403 retry) değilse ve localStorage dolu ise onu kullan; boş veya forceTillRefresh ise tillService'e git
            if (!forceTillRefresh) {
                const cachedToken = localStorage.getItem(GeneralUtils.TILL_JWT_CACHE_KEY);
                if (cachedToken) return cachedToken.startsWith('Bearer ') ? cachedToken : 'Bearer ' + cachedToken;
            }

            let result;
            try {
                if (!GeneralUtils.tillTokenFetchPromise) {
                    const tillService = new TillService();
                    GeneralUtils.tillTokenFetchPromise = tillService.getTokens()
                        .finally(() => { GeneralUtils.tillTokenFetchPromise = null; });
                }
                result = await GeneralUtils.tillTokenFetchPromise;
            } catch (e) {
                console.warn("Till token could not be retrieved:", e);
                GeneralUtils.clearCookieAndLocalStorage();
                showSessionExpiredPage();
                return null;
            }

            if (!result || !result.accessToken) {
                console.warn("Till token could not be retrieved!");
                GeneralUtils.clearCookieAndLocalStorage();
                showSessionExpiredPage();
                return null;
            }

            let rawToken = result.accessToken;
            if (rawToken?.startsWith("Bearer ")) rawToken = rawToken.slice(7);
            const cleanedToken = rawToken?.replace(/\s+/g, '');

            let remainingTime = null;
            try {
                const parts = (cleanedToken || rawToken).split('.');
                if (parts.length === 3) {
                    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                    if (payload.exp) {
                        remainingTime = (payload.exp * 1000) - Date.now();
                    }
                }
            } catch (e) { /* ignore */ }

            if (!remainingTime && result.expireDate && typeof result.expireDate === 'string') {
                remainingTime = new Date(result.expireDate).getTime() - Date.now();
            }
            if (!remainingTime && result.expiresIn != null) {
                remainingTime = result.expiresIn * 1000;
            }

            if (remainingTime != null && remainingTime <= 0) {
                console.warn("Till token expired. Redirecting to token expired.");
                GeneralUtils.clearCookieAndLocalStorage();
                showSessionExpiredPage();
                return null;
            }

            const bearerToken = "Bearer " + cleanedToken;
            try {
                localStorage.setItem(GeneralUtils.TILL_JWT_CACHE_KEY, bearerToken);
                localStorage.setItem(GeneralUtils.TILL_JWT_CACHE_TIMESTAMP_KEY, String(Date.now()));
            } catch (e) { /* ignore */ }
            return bearerToken;
        }

        // 2️⃣ Merchant User
        if (userType === "merchant") {
            const accessToken = localStorage.getItem("json_token");
            const refreshToken = localStorage.getItem("refresh_token");
            const expireDateStr = localStorage.getItem("expireDate");

            if (!accessToken || !refreshToken || !expireDateStr) {
                console.warn("Token or refresh token is missing!");
                GeneralUtils.clearCookieAndLocalStorage();
                window.location = "/";
                return null;
            }

            const remainingTime = new Date(expireDateStr).getTime() - new Date().getTime();

            // Token süresi geçmişse logout
            if (remainingTime <= 0) {
                console.warn("Token expired. Logging out.");
                GeneralUtils.clearCookieAndLocalStorage();
                window.location = "/";
                return null;
            }

            // 120 saniye veya daha az kaldıysa refresh yap
            if (remainingTime <= 120 * 1000) {
                if (!GeneralUtils.tokenRefreshPromise) {
                    const merchantService = new MerchantUserSecurityService();
                    GeneralUtils.tokenRefreshPromise = merchantService.refreshToken()
                        .then(response => {
                            if (response && !response.type && response.accessToken) {
                                return GeneralUtils.setJwtToCookie(
                                    response.accessToken,
                                    response.refreshToken,
                                    response.expireDate
                                ).then(() => response.accessToken);
                            }
                            GeneralUtils.clearCookieAndLocalStorage();
                            window.location = "/";
                        })
                        .catch(err => {
                            console.error("Merchant token refresh error:", err);
                            GeneralUtils.clearCookieAndLocalStorage();
                            window.location = "/";
                        })
                        .finally(() => {
                            GeneralUtils.tokenRefreshPromise = null;
                        });
                }
                return GeneralUtils.tokenRefreshPromise;
            }

            return accessToken;
        }

        // 3️⃣ Admin User
        if (userType === "admin") {
            const accessToken = localStorage.getItem("json_token");
            const refreshToken = localStorage.getItem("refresh_token");
            const expireDateStr = localStorage.getItem("expireDate");

            if (!accessToken || !refreshToken || !expireDateStr) {
                console.warn("Admin token or refresh token is missing!");
                GeneralUtils.clearCookieAndLocalStorage();
                window.location = "/";
                return null;
            }

            const remainingTime = new Date(expireDateStr).getTime() - new Date().getTime();

            // Token süresi geçmişse logout
            if (remainingTime <= 0) {
                console.warn("Admin token expired. Logging out.");
                GeneralUtils.clearCookieAndLocalStorage();
                window.location = "/";
                return null;
            }

            // 120 saniye veya daha az kaldıysa refresh yap
            if (remainingTime <= 120 * 1000) {
                if (!GeneralUtils.adminTokenRefreshPromise) {
                    const adminService = new AdminUserSecurityService();
                    GeneralUtils.adminTokenRefreshPromise = adminService.refreshToken()
                        .then(response => {
                            if (response && !response.type && response.accessToken) {
                                return GeneralUtils.setJwtToCookie(
                                    response.accessToken,
                                    response.refreshToken,
                                    response.expireDate
                                ).then(() => response.accessToken);
                            }
                            GeneralUtils.clearCookieAndLocalStorage();
                            window.location = "/";
                        })
                        .catch(err => {
                            console.error("Admin token refresh error:", err);
                            GeneralUtils.clearCookieAndLocalStorage();
                            window.location = "/";
                        })
                        .finally(() => {
                            GeneralUtils.adminTokenRefreshPromise = null;
                        });
                }
                return GeneralUtils.adminTokenRefreshPromise;
            }

            return accessToken;
        }

        // 4️⃣ Diğer durumlar
        const token = localStorage.getItem('json_token');
        return token || null;
    }







    static getRefreshTokenFromCookie() {
        return localStorage.getItem('refresh_token');

    }

    static async setJwtToCookie(token, refreshToken, expireDate) {
        localStorage.setItem("json_token", token);
        localStorage.setItem("refresh_token", refreshToken);
        localStorage.setItem("expireDate", expireDate);

    }

    static responseToJson(response) {
        if (!GeneralUtils.isHttpResponse(response)) {
            return Promise.resolve({});
        }
        return response.text().then(text => {
            if (!text || !text.trim()) return {};
            try {
                return JSON.parse(text);
            } catch (e) {
                return {};
            }
        });
    }

    /** TillUser için 401 veya 403'te otomatik token yenileyip bir kez tekrar dener. Tüm auth'lı isteklerde kullanın. */
    static async fetchWithAuth(url, options = {}) {
        if (typeof window !== 'undefined' && window.__FETCH_WITH_AUTH_DEBUG) console.warn('[fetchWithAuth]', url);
        const headers = { ...(options.headers || {}), Authorization: (await GeneralUtils.getJwtFromCookieOrTillService()) };
        let response = await fetch(url, { ...options, headers });
        const isTillUser = localStorage.getItem("userType") === "tillUser";

        if ((response.status === 401 || response.status === 403) && isTillUser) {
            const newToken = await GeneralUtils.getJwtFromCookieOrTillService({ forceTillRefresh: true });
            if (newToken) response = await fetch(url, { ...options, headers: { ...headers, Authorization: newToken } });
        }
        return response;
    }

    static validateEmail(email) {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    static onValidationFailErrorFile = (e) => {
        toast.error(e.detail);
    }

    static removeDuplicates = (arr) => {
        const uniqueIds = [];
        const unique = arr.filter(element => {
            const isDuplicate = uniqueIds.includes(element.id);
            if (!isDuplicate) {
                uniqueIds.push(element.id);
                return true;
            }
            return false;
        });
        return unique;
    }

    static notification(response) {
        this.notificationMessage(response, false, true)
    }

    static notificationPayNoAttention(response) {
        this.notificationMessage(response, false, false)
    }

    static notification(response, hiddenSuccess) {
        this.notificationMessage(response, hiddenSuccess, true)
    }

    static isHttpResponse(response) {
        return response != null
            && typeof response.text === 'function'
            && typeof response.status === 'number';
    }

    static safeJson(response) {
        if (!GeneralUtils.isHttpResponse(response)) {
            return Promise.resolve({});
        }
        return response.text().then(text => {
            try {
                return text ? JSON.parse(text) : {};
            } catch (e) {
                console.warn("Failed to parse JSON:", e);
                return {};
            }
        });
    }



    static notificationMessage(response, hiddenSuccess, payNoAttention) {

        if (!GeneralUtils.isHttpResponse(response)) {
            if (payNoAttention === true && !response) {
                if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("userType")) &&
                    (localStorage.getItem("userType") === "admin"
                    )
                ) {
                    GeneralUtils.clearCookieAndLocalStorage();
                    window.location = "";
                }
            }
            return;
        }

        if (payNoAttention === true && !response.status) {
            if (!response) {
                if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("userType")) &&
                    (localStorage.getItem("userType") === "admin"
                        //|| localStorage.getItem("userType") === "merchant"
                    )
                ) {
                    GeneralUtils.clearCookieAndLocalStorage();
                    window.location = "";
                    return;
                }
            }

            if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("userType")) &&
                (localStorage.getItem("userType") === "admin"
                    // || localStorage.getItem("userType") === "merchant"
                )
            ) {
                GeneralUtils.clearCookieAndLocalStorage();
                window.location = "";
                return;
            }
        } else {
            GeneralUtils.safeJson(response).then(function(notification) {
                switch (response.status) {
                    case 200:
                        if (notification && notification.type) {
                            GeneralUtils.showNotification(notification);
                        } else if (!hiddenSuccess) {
                            toast.success("The operation was performed successfully.");
                        }
                        break;

                    case 400:
                        if (notification && notification.type) {
                            GeneralUtils.showNotification(notification);
                        } else {
                            toast.warn("Empty Values.");
                        }
                        break;

                    case 401:
                        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("userType")) &&
                            ( localStorage.getItem("userType") === "admin")
                        ) {
                            if (notification && notification.type) {
                                GeneralUtils.showNotification(notification);
                                GeneralUtils.clearCookieAndLocalStorage();
                            } else {
                                toast.warn("Not authorized");
                            }
                        } else {
                            GeneralUtils.clearCookieAndLocalStorage();
                        }
                        break;

                    case 403:
                        console.log("Not Authorized");
                        break;

                    case 404:
                        if (notification && notification.type) {
                            GeneralUtils.showNotification(notification);
                        } else {
                            toast.warn("Error");
                        }
                        break;

                    case 409:
                        if (notification && notification.type) {
                            GeneralUtils.showNotification(notification);
                        } else {
                            toast.warn("Duplicate Value");
                        }
                        break;

                    case 412:
                        if (notification && notification.type) {
                            GeneralUtils.showNotification(notification);
                        } else {
                            toast.warn("PreCondition Failed");
                        }
                        break;

                    case 503:
                        toast.warn("Server is down. Please try again in a few minutes.");
                        break;

                    default:
                        if (payNoAttention === false) {
                            console.log(response);
                        } else {
                            console.log(response);
                            toast.error("An unexpected error has occurred. Error code: " + response.status);
                        }
                        break;
                }
            });
        }
    }



    static showNotification(notification) {

        switch (notification.type) {
            case "SUCCESS":
                toast.success(notification.details);
                break;
            case "INFO" :
                toast.info(notification.details);
                break;
            case "WARNING" :
                toast.warn(notification.details);
                break;
            case "ERROR":
                toast.error(notification.details);
                break;
            default:
                toast.error("An unexpected error has occurred. " + notification.details);
                break;
        }
    }

    static getUserNameAndFamilyNames() {
        var hsmUserLocalStorage = JSON.parse(localStorage.getItem("hsmUser"));
        return (!this.__isValueEmpty(hsmUserLocalStorage)) ? (GeneralUtils.isNullOrEmpty(hsmUserLocalStorage.firstName) ? "" : hsmUserLocalStorage.firstName) + ' ' + (GeneralUtils.isNullOrEmpty(hsmUserLocalStorage.lastName) ? "" : hsmUserLocalStorage.lastName) : '';
    }

    static getUserDisplayName() {
        const fullName = (this.getUserNameAndFamilyNames() || '').trim();
        if (fullName) {
            return fullName;
        }
        const firstName = (this.getFirstName() || '').trim();
        if (firstName) {
            return firstName;
        }
        const userName = (this.getUserName() || '').trim();
        if (userName) {
            return userName;
        }
        return 'User';
    }

    static getUserInitials() {
        try {
            const hsmUser = JSON.parse(localStorage.getItem("hsmUser"));
            if (hsmUser) {
                const first = (hsmUser.firstName || '').trim();
                const last = (hsmUser.lastName || '').trim();
                if (first && last) {
                    return (first.charAt(0) + last.charAt(0)).toUpperCase();
                }
                if (first) {
                    return first.charAt(0).toUpperCase();
                }
                if (last) {
                    return last.charAt(0).toUpperCase();
                }
            }
        } catch (e) {
            // ignore parse errors
        }
        const userName = (this.getUserName() || '').trim();
        if (userName) {
            if (userName.indexOf('@') > 0) {
                return userName.charAt(0).toUpperCase();
            }
            const parts = userName.split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
                return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
            }
            return userName.charAt(0).toUpperCase();
        }
        return '?';
    }

    static getUserGroupLabel() {
        const group = this.getUserGroup();
        if (this.isNullOrEmpty(group)) {
            return '';
        }
        return String(group).replace(/_/g, ' ');
    }

    static isAuthenticated() {
        var isAuthenticated = JSON.parse(localStorage.getItem("isAuthenticated"));
        return (!this.__isValueEmpty(isAuthenticated)) ? isAuthenticated : false;
    }

    static hasWebMenuLicense() {
        var result = JSON.parse(localStorage.getItem("WEB_MENU_LICENSE"));
        return (!this.__isValueEmpty(result)) ? result : false;
    }

    static hasWebOrderLicense() {
        var result = JSON.parse(localStorage.getItem("WEB_ORDER_LICENSE"));
        return (!this.__isValueEmpty(result)) ? result : false;
    }

    static hasTableOrderLicense() {
        var result = JSON.parse(localStorage.getItem("TABLE_ORDER_LICENSE"));
        return (!this.__isValueEmpty(result)) ? result : false;
    }


    static getFirstName() {
        var hsmUserLocalStorage = JSON.parse(localStorage.getItem("hsmUser"));
        return (!this.__isValueEmpty(hsmUserLocalStorage)) ? hsmUserLocalStorage.firstName : '';
    }

    static getUserName() {
        var hsmUserLocalStorage = JSON.parse(localStorage.getItem("hsmUser"));
        return (!this.__isValueEmpty(hsmUserLocalStorage)) ? hsmUserLocalStorage.userName : ' ';
    }

    static getUserRoleList() {
        var hsmUserLocalStorage = JSON.parse(localStorage.getItem("hsmUser"));
        return (!this.__isValueEmpty(hsmUserLocalStorage)) ? hsmUserLocalStorage.roles : null;
    }

    static getUserGroup() {
        var hsmUserLocalStorage = JSON.parse(localStorage.getItem("hsmUser"));
        return (!this.__isValueEmpty(hsmUserLocalStorage) && !this.__isValueEmpty(hsmUserLocalStorage.group))
            ? hsmUserLocalStorage.group
            : "MERCHANT_BRANCH_USER";
    }

    static isClerk() {
        return this.hasRole("CLERK");
    }

    static isKitchen() {
        return this.hasRole("KITCHEN");
    }

    static isDriver() {
        return this.hasRole("DRIVER");
    }

    static hasRole(roleName) {
        var hasRole = false;
        if (localStorage.getItem("scope") == null) {
            var list = this.getUserRoleList();
            if (list != null) {
                for (var i = 0; i < list.length; i++) {
                    if (list[i].name === roleName) {
                        hasRole = true;
                    }
                }
            }
            var userGroup = this.getUserGroup();
            if (userGroup == "MERCHANT_BRANCH_ADMIN") {
                hasRole = true;
            }

        } else {
            if (localStorage.getItem("scope") && !GeneralUtils.isNullOrEmpty(localStorage.getItem("scope")) && localStorage.getItem("scope").indexOf(roleName) >= 0) {
                hasRole = true;
            }
        }
        return hasRole;
    }

    static isWaiter() {
        return this.hasRole("WAITER");
    }

    static getPrivilegeListFromRoleList() {
        var hsmRoleList = this.getUserRoleList();
        let merchantRolePrivilegeList = []
        hsmRoleList.forEach((element) => {
            element.merchantRolePrivilegeList.forEach((privilege) => {
                merchantRolePrivilegeList.push(privilege);
            });
        });
        return (!this.__isValueEmpty(merchantRolePrivilegeList)) ? merchantRolePrivilegeList : null;
    }

    static getPrivilegeList() {
        var hsmPrivilegeList = JSON.parse(localStorage.getItem("hsmPrivilegeList"));
        return this.isNullOrEmpty(hsmPrivilegeList) ? null : hsmPrivilegeList;
    }

    static checkPrivilege(object) {
        var hsmPrivilegeList = this.getPrivilegeList();
        if (hsmPrivilegeList != null) {
            for (var element of hsmPrivilegeList) {
                if (element == object) {
                    return true;
                }
            }
        }
        return false;
    }


    static getDialogHeader(objectName, pageMode) {
        if (pageMode === PAGE_MODE.VIEW) {
            return objectName + ' View';
        } else if (pageMode === PAGE_MODE.UPDATE) {
            return objectName + '  Update';
        } else if (pageMode === PAGE_MODE.CREATE) {
            return 'New ' + objectName;
        }
    }

    static randomID() {
        return Math.random().toString(36).substr(2, 16);
    }

    static getReplaceValue(value) {
        return value.toString().replace("(", "").replace(")", "")
            .replace(" ", "").replace("-", "");
    }

    static returnMainPage() {
        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("orgHref"))) {
            let url = localStorage.getItem("orgHref");
            url = url.substr(0, url.indexOf("?"));
            window.location.href = url;
        }
    }

    static getUiUrl() {
        if (!GeneralUtils.isNullOrEmpty(localStorage.getItem("orgHref"))) {
            let url = localStorage.getItem("orgHref");
            if (url.indexOf("?") > 0) {
                url = url.substr(0, url.indexOf("?"));
            } else {
                if (url.indexOf("/#/") > 0) {

                } else {
                    url = url.substr(0, url.length);
                }

            }
            return url.toString();
        } else {
            return window.location.href;
        }
    }

    static addQueryParam = (key, value) => {
        const url = new URL(window.location.href);
        url.searchParams.set(key, value);
        window.history.pushState({}, '', url.toString());
    };

    static removeQueryParam = (key) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(key);
        window.history.pushState({}, '', url.toString());
    };

    static getQueryParam = (key) => {
        const url = new URL(window.location.href);
        return url.searchParams.get(key) || '';
    };

    static removeQueryString(url) {
        const urlObj = new URL(url);
        urlObj.search = '';
        return urlObj.toString();
    }

    static isServedInWebMarketUrl() {
        let url = window.location.href;
        if (url.indexOf("dispatchio") > 0 || url.indexOf("posmarket") > 0) {
            return true;
        } else {
            return false;
        }
    }

    static isTestEnvironment() {
        return SERVER_URL == "https://admin.posmarket.site/service/" ? true : false;
    }

    static isProductionEnvironment() {
        return SERVER_URL == "https://admin.dispatchio.store/service/" ? true : false;
    }

    static isLocalEnvironment() {
        return !GeneralUtils.isProductionEnvironment() && !GeneralUtils.isTestEnvironment();
    }

    static getEnvironmentBanner() {
        if (GeneralUtils.isProductionEnvironment()) {
            return null;
        }
        if (GeneralUtils.isTestEnvironment()) {
            return {
                type: 'test',
                label: 'TEST Environment',
                subtitle: 'Non-production data'
            };
        }
        return {
            type: 'local',
            label: 'LOCAL Environment',
            subtitle: 'Development only'
        };
    }

    /**
     * DNA hosted-fields `isTestMode` / sandbox return URLs (test-pay.dnapayments.com success/failure).
     * - `SERVER_URL` prod değilse sandbox.
     * - Tarayıcı hostname `posmarket` içeriyorsa (posmarket.site, admin.posmarket.site) her zaman sandbox:
     *   aksi halde Constants yanlışlıkla prod iken bile merchant SPA iframe + X-Frame-Options kırılır.
     */
    static isDnaHostedFieldsTestMode() {
        if (typeof window !== "undefined" && window.location) {
            const h = (window.location.hostname || "").toLowerCase();
            if (h === "localhost" || h === "127.0.0.1" || h === "[::1]") {
                return true;
            }
            if (h.includes("posmarket")) {
                return true;
            }
        }
        return !GeneralUtils.isProductionEnvironment();
    }

    /** Apple Pay sheet: success or failure after payment authorization. */
    static completeApplePayPayment(session, success) {
        if (!session || typeof window === "undefined" || !window.ApplePaySession) {
            return;
        }
        try {
            session.completePayment(success
                ? window.ApplePaySession.STATUS_SUCCESS
                : window.ApplePaySession.STATUS_FAILURE);
        } catch (ignore) {
            // Session may already be completed or cancelled.
        }
    }

    /** Apple Pay sheet: abort when merchant validation cannot complete. */
    static abortApplePaySession(session) {
        if (!session) {
            return;
        }
        try {
            session.abort();
        } catch (ignore) {
            // Session may already be completed or cancelled.
        }
    }

    static getDomainName() {
        let url = window.location.href;
        //return "store"

        if (url.includes("fbclid")) {
            url = this.removeQueryString(url);
            window.location.href = url;
        }
        url = url.split('?')[0];
        var domainName = url.replace("/login", "").replace("/paymentMethods", "").replace("/approveOrder", "").replace('http://', '').replace("https://", "").replace("#/", "").replace("/#/", "").replace("/", ".").replace("#", "").replace("www.", "").replace("order.", "").replace(":3000", "").split(".");
        if (domainName[0] == "dispatchio" || domainName[0] == "posmarket") {
            var result = domainName[domainName.length - 1].split(".");
            if (window.location.href.includes("accessToken")) {
                return null;
            } else {
                return result[result.length - 1] == "marketplace" ? "" : result[result.length - 1].replace("/", "");
            }

        } else {
            return domainName[0].replace("/", "");
        }

    }

    static sliceString(data, length) {
        if (data.length > length) {
            return data.slice(0, length) + " ..."
        } else
            return data;

    }

    static removeSlashes = (value) => {
        if (value != undefined) {
            return value.replace('/', '').replace('\\\\', '').replace('"', '');
        }

    }

    static getMonday(date = new Date()) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - ((day + 6) % 7);
        d.setDate(diff);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    static getStartOfToday() {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    static getEndOfToday() {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d;
    }

    static getThisMonthRange() {
        const start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);

        const end = this.getEndOfToday();
        return [start, end];
    }

    static getLastMonthRange() {
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);

        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);

        return [start, end];
    }

    static getLastWeekRange() {
        const now = new Date();
        const currentMonday = this.getMonday(now);

        const lastWeekMonday = new Date(currentMonday);
        lastWeekMonday.setDate(currentMonday.getDate() - 7);
        lastWeekMonday.setHours(0, 0, 0, 0);

        const lastWeekSunday = new Date(currentMonday);
        lastWeekSunday.setDate(currentMonday.getDate() - 1);
        lastWeekSunday.setHours(23, 59, 59, 999);

        return [lastWeekMonday, lastWeekSunday];
    }

    static getLast7DaysRange() {
        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - 6);
        start.setHours(0, 0, 0, 0);

        const end = this.getEndOfToday();
        return [start, end];
    }

    /**
     * Order total as a number in major currency units (e.g. GBP). DNA payment JSON expects `amount` as a numeric float, not a string.
     */
    static parseDnaAmountMajorUnits(totalPrice) {
        if (totalPrice == null || totalPrice === "") {
            return null;
        }
        const n = typeof totalPrice === "number"
            ? totalPrice
            : parseFloat(String(totalPrice).replace(/,/g, "").trim());
        if (!Number.isFinite(n) || n <= 0) {
            return null;
        }
        return Math.round(n * 100) / 100;
    }

    /**
     * DNA Hosted Fields: browser 3DS sonrası returnUrl / failureReturnUrl.
     * 3DS akışı iframe içinde merchant SPA’yı (ör. admin.*) yüklemeye çalışırsa X-Frame-Options: deny ile kırılır;
     * sandbox modda DNA’nın statik success/failure sayfaları kullanılır. Webhook/callbackUrl ayrıca backend’e gider.
     * @param {string} baseUi
     * @param {string} approveRelativePath
     * @param {string} failRelativePath
     * @param {{ dnaTestMode?: boolean }} [options] dnaTestMode true iken (veya localhost) sandbox return URL’leri
     */
    static getDnaHostedFieldsReturnUrls(baseUi, approveRelativePath, failRelativePath, options) {
        const opts = options || {};
        const h = typeof window !== "undefined" && window.location ? window.location.hostname : "";
        const isLocalhost = h === "localhost" || h === "127.0.0.1" || h === "[::1]";
        const envSandbox =
            typeof process !== "undefined" && process.env && process.env.REACT_APP_DNA_USE_SANDBOX_RETURN_URLS === "true";
        const envMerchant =
            typeof process !== "undefined" && process.env && process.env.REACT_APP_DNA_USE_SANDBOX_RETURN_URLS === "false";
        // If code explicitly says we're in DNA test mode, always use sandbox return URLs.
        // (Some deployments set REACT_APP_DNA_USE_SANDBOX_RETURN_URLS=false, which would otherwise force merchant URLs
        // and break 3DS iframe when admin.* sends X-Frame-Options: deny.)
        const useSandboxReturns =
            envSandbox || isLocalhost || opts.dnaTestMode === true;
        const useMerchantReturns = envMerchant === true && opts.dnaTestMode !== true && !isLocalhost && !envSandbox;

        if (useSandboxReturns && !useMerchantReturns) {
            return {
                returnUrl: "https://test-pay.dnapayments.com/checkout/success.html",
                failureReturnUrl: "https://test-pay.dnapayments.com/checkout/failure.html"
            };
        }
        const base = (baseUi || "").replace(/\/$/, "");
        const okPath = (approveRelativePath || "").startsWith("/") ? approveRelativePath : "/" + (approveRelativePath || "");
        const badPath = (failRelativePath || "").startsWith("/") ? failRelativePath : "/" + (failRelativePath || "");
        return {
            returnUrl: base + okPath,
            failureReturnUrl: base + badPath
        };
    }

    /**
     * DNA hosted-fields submit() bazen düz nesne, bazen result / payment / data içinde döner.
     */
    static unwrapDnaHostedFieldsSubmitResponse(res) {
        if (GeneralUtils.isNullOrEmpty(res) || typeof res !== "object") {
            return res;
        }
        const inner = res.result || res.payment || res.data || res.response || res.body;
        if (inner && typeof inner === "object" && inner !== res) {
            return Object.assign({}, res, inner);
        }
        return res;
    }

    static extractDnaHostedFieldsTransactionRef(res) {
        const u = GeneralUtils.unwrapDnaHostedFieldsSubmitResponse(res);
        if (GeneralUtils.isNullOrEmpty(u) || typeof u !== "object") {
            return null;
        }
        const id = u.transactionId || u.paymentId || u.id;
        return id != null && String(id).trim() !== "" ? String(id).trim() : null;
    }

    /** Log DNA state after hosted-fields submit; server capture expects PREAUTH_SUCCESS / AUTHORISED (or AUTH) per DNA. */
    static logDnaHostedFieldsPreCaptureState(res) {
        try {
            const u = GeneralUtils.unwrapDnaHostedFieldsSubmitResponse(res);
            console.info("DNA hosted-fields submit (before server capture):", {
                transactionState: u && u.transactionState,
                paymentState: u && u.paymentState,
                status: u && u.status,
                success: u && u.success,
                transactionId: GeneralUtils.extractDnaHostedFieldsTransactionRef(res),
            });
        } catch (e) { /* ignore */ }
    }

    static isDnaHostedFieldsSuccess(res) {
        const u = GeneralUtils.unwrapDnaHostedFieldsSubmitResponse(res);
        if (GeneralUtils.isNullOrEmpty(u) || typeof u !== "object") {
            return false;
        }
        const statusLc = u.status != null ? String(u.status).toLowerCase().trim() : "";
        return (
            u.success === true ||
            String(u.responseCode != null ? u.responseCode : "").trim() === "00" ||
            Number(u.errorCode) === 0 ||
            statusLc === "approved" ||
            statusLc === "succeeded" ||
            statusLc === "success" ||
            statusLc === "complete" ||
            statusLc === "completed"
        );
    }

    /**
     * True if this member card row was saved for DNA token wallet ({@code cardType} like {@code DNA|11}).
     */
    static isDnaTokenWalletMemberCard(card) {
        if (GeneralUtils.isNullOrEmpty(card) || GeneralUtils.isNullOrEmpty(card.cardType)) {
            return false;
        }
        return String(card.cardType).startsWith("DNA|");
    }

    /**
     * Normalize expiry for DNA hosted-fields {@code selectCard} (prefers MM/YY).
     */
    static normalizeDnaExpiryForSelectCard(expireDate) {
        if (GeneralUtils.isNullOrEmpty(expireDate)) {
            return "12/99";
        }
        const s = String(expireDate).trim();
        if (s.includes("/")) {
            return s;
        }
        if (/^\d{4}$/.test(s)) {
            return s.substring(0, 2) + "/" + s.substring(2);
        }
        return s;
    }

    /**
     * Build {@code selectCard} argument from MemberUserCard row (see DNA hosted-fields token wallet).
     */
    static memberUserCardToDnaSelectCard(item) {
        if (!GeneralUtils.isDnaTokenWalletMemberCard(item) || GeneralUtils.isNullOrEmpty(item.crossReference)) {
            return null;
        }
        const raw = String(item.cardType);
        const schemeId = raw.length > 4 ? raw.slice(4).trim() : "";
        return {
            merchantTokenId: String(item.crossReference).trim(),
            panStar: item.cardNo || "**** **** **** ****",
            cardSchemeId: schemeId || "0",
            cardSchemeName: item.cardHolderName || "CARD",
            cardName: item.cardHolderName || "Saved card",
            expiryDate: GeneralUtils.normalizeDnaExpiryForSelectCard(item.expireDate),
            isCSCRequired: true,
            cscState: "required",
        };
    }

    /**
     * Extract token-wallet fields from hosted-fields submit response for server-side save after capture.
     */
    static extractDnaTokenWalletSaveFields(res) {
        const u = GeneralUtils.unwrapDnaHostedFieldsSubmitResponse(res);
        if (GeneralUtils.isNullOrEmpty(u) || typeof u !== "object") {
            return null;
        }
        const pick = (...keys) => {
            for (let i = 0; i < keys.length; i++) {
                const k = keys[i];
                if (u[k] != null && String(u[k]).trim() !== "") {
                    return String(u[k]).trim();
                }
            }
            return null;
        };
        const merchantTokenId = pick(
            "cardTokenId",
            "merchantTokenId",
            "tokenId",
            "paymentMethodToken",
            "savedCardTokenId"
        );
        if (GeneralUtils.isNullOrEmpty(merchantTokenId)) {
            return null;
        }
        return {
            dnaMerchantTokenId: merchantTokenId,
            // Some DNA responses use "cardPanStarred" (seen in /transaction/{id}/search) or "panStar".
            // Fall back to "****" only if nothing else is provided.
            dnaPanStar: pick(
                "panStar",
                "cardPanStarred",
                "cardPanStar",
                "maskedPan",
                "maskedNumber",
                "maskedCardNumber",
                "cardNumberMasked"
            ) || "****",
            dnaCardSchemeId: pick("cardSchemeId", "schemeId", "cardScheme", "cardSchemeID") || "0",
            dnaCardSchemeName: pick("cardSchemeName", "schemeName", "cardBrand", "paymentMethod", "cardScheme") || "CARD",
            dnaTokenExpiryDisplay: pick("expiryDate", "cardExpiryDate", "cardExpiry", "expiry", "expirationDate"),
        };
    }


}
